import React, { useContext, useState } from 'react';
import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  FieldTimeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Popconfirm, Space, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import AfterHoursDrawer from '@totem/components/portfolio/buildings/hvac/afterHours/AfterHoursDrawer';
import HVACScheduleDrawer from '@totem/components/portfolio/buildings/hvac/HVACScheduleDrawer';
import HVACSpaceModal from '@totem/components/portfolio/buildings/hvac/HVACSpaceModal';
import HVACSpacesContext from '@totem/components/portfolio/buildings/hvac/HVACSpacesContext';
import {
  HVACSpace,
  SPACE_TYPES,
} from '@totem/components/portfolio/buildings/hvac/types';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const spaceTypeLabel = (value: string) => {
  const match = SPACE_TYPES.find((spaceType) => spaceType.value === value);
  return match ? match.label : value;
};

const HVACSpacesPanel = () => {
  const { getApiToken } = useContext(UserProfileContext);
  const { data, loading, buildingId, requestRefresh } =
    useContext(HVACSpacesContext);

  const [modalOpen, setModalOpen] = useState(false);
  const [editSpace, setEditSpace] = useState<HVACSpace>(null);
  const [scheduleSpace, setScheduleSpace] = useState<HVACSpace>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [afterHoursSpace, setAfterHoursSpace] = useState<HVACSpace>(null);
  const [afterHoursOpen, setAfterHoursOpen] = useState(false);

  const handleDelete = (space: HVACSpace) => {
    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/hvac-spaces/${space.id}`,
      {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    ).then((res) => {
      CheckResponseShowError(res);

      requestRefresh();
    });
  };

  const columns: ColumnProps<HVACSpace>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (compA, compB) => sortStringAscending(compA.name, compB.name),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (_, record) => spaceTypeLabel(record.type),
      sorter: (compA, compB) => sortStringAscending(compA.type, compB.type),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Sq Ft',
      dataIndex: 'squareFeet',
      key: 'squareFeet',
      render: (_, record) =>
        isNotNull(record.squareFeet) ? record.squareFeet.toLocaleString() : '—',
    },
    {
      title: 'Occupied',
      dataIndex: 'occupied',
      key: 'occupied',
      render: (_, record) => (
        <Tag color={record.occupied ? 'green' : 'default'}>
          {record.occupied ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Equipment',
      dataIndex: 'primaryEquipment',
      key: 'primaryEquipment',
      render: (_, record) =>
        (record.primaryEquipment ?? []).map((eq) => <Tag key={eq}>{eq}</Tag>),
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<CalendarOutlined />}
            onClick={() => {
              setScheduleSpace(record);

              setScheduleOpen(true);
            }}
          >
            Schedules
          </Button>
          <Button
            size="small"
            icon={<FieldTimeOutlined />}
            onClick={() => {
              setAfterHoursSpace(record);

              setAfterHoursOpen(true);
            }}
          >
            After Hours
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditSpace(record);

              setModalOpen(true);
            }}
          />
          <Popconfirm
            title="Delete this space?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <IBCard
        isCovered
        title="HVAC Spaces"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditSpace(null);

              setModalOpen(true);
            }}
          >
            Add Space
          </Button>
        }
      >
        <IBTable
          columns={columns}
          dataSource={isNotNull(data) ? data : []}
          loading={loading}
          rowKey={(record) => record.id}
          pagination={{ showSizeChanger: true }}
        />
      </IBCard>

      <HVACSpaceModal
        open={modalOpen}
        space={editSpace}
        onClose={() => {
          setModalOpen(false);

          setEditSpace(null);
        }}
      />

      {isNotNull(scheduleSpace) && (
        <HVACScheduleDrawer
          open={scheduleOpen}
          space={scheduleSpace}
          onClose={() => {
            setScheduleOpen(false);

            setScheduleSpace(null);
          }}
        />
      )}

      {isNotNull(afterHoursSpace) && (
        <AfterHoursDrawer
          open={afterHoursOpen}
          buildingId={buildingId}
          space={afterHoursSpace}
          onClose={() => {
            setAfterHoursOpen(false);

            setAfterHoursSpace(null);
          }}
        />
      )}
    </>
  );
};

export default HVACSpacesPanel;
